// src/pages/Terms.js
import React from 'react';

const Terms = () => {
  return (
    <div className="page-container">
      <div className="last-updated">
        <strong>Last Updated:</strong> June 24, 2026
      </div>

      <h1>Terms of Service (EULA)</h1>

      <div className="highlight-box">
        <h3>Agreement Overview</h3>
        <p>
          By using AI Vision App, you agree to these Terms of Service. Please read them
          carefully as they contain important information about your rights and obligations.
        </p>
      </div>

      <div className="legal-section">
        <h2>1. Acceptance of Terms</h2>

        <p>
          These Terms of Service ("Terms", "EULA") govern your use of the AI Vision App mobile
          application ("App") provided by AI Vision Team ("we," "us," or "our").
        </p>

        <p>
          By downloading, installing, or using the App, you agree to be bound by these
          Terms. If you do not agree to these Terms, do not use the App.
        </p>

        <div className="warning-box">
          <h3>Legal Agreement</h3>
          <p>
            These Terms constitute a legally binding agreement between you and AI Vision Team.
            You must be at least 13 years old to use this service.
          </p>
        </div>
      </div>

      <div className="legal-section">
        <h2>2. Description of Service</h2>

        <h3>2.1 AI Vision App Service</h3>
        <p>
          AI Vision App is an artificial intelligence-powered mobile application that
          provides real-time object recognition and analysis through your device's camera,
          powered by Google's Gemini AI model.
        </p>

        <h3>2.2 Core Features (Free Tier)</h3>
        <ul className="legal-list">
          <li>Real-time object detection with AR-style floating labels</li>
          <li>6 detection modes: Object, Text, Barcode/QR, Plant, Food, Landmark</li>
          <li>Up to 5 AI-powered scans per day per device</li>
          <li>Up to 3 objects detected per scan</li>
          <li>Basic object information (label, description, confidence score)</li>
          <li>Detection history (metadata saved automatically)</li>
          <li>Actions: search web, share, speak, translate, save to favorites</li>
          <li>Barcode/QR code scanning with URL opening and value copying</li>
          <li>Advertisements displayed via Google AdMob</li>
        </ul>

        <h3>2.3 Premium Features</h3>
        <p>Additional features available through paid subscription:</p>
        <ul className="legal-list">
          <li>Unlimited AI-powered scans (no daily limit)</li>
          <li>Up to 5 objects detected per scan with faster detection intervals (1 second)</li>
          <li>Enhanced details: fun facts, estimated pricing</li>
          <li>Cloud image backup via Firebase Storage</li>
          <li>Ad-free experience</li>
          <li>Batch capture mode</li>
          <li>Data export (PDF, CSV)</li>
          <li>Voice commands (capture, switch camera, toggle flash)</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>3. Auto-Renewable Subscription Terms</h2>

        <div className="highlight-box" style={{ borderLeft: '4px solid #8B5CF6' }}>
          <h3>Premium Subscription Plans</h3>
          <p>AI Vision App offers the following auto-renewable subscription options:</p>
        </div>

        <h3>3.1 Subscription Options</h3>
        <ul className="legal-list">
          <li>
            <strong>Monthly Premium Subscription:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
              <li>Title: AI Vision App Monthly Premium</li>
              <li>Price: $3.99 USD per month (price may vary by region)</li>
              <li>Duration: 1 month</li>
            </ul>
          </li>
          <li>
            <strong>Yearly Premium Subscription:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
              <li>Title: AI Vision App Yearly Premium</li>
              <li>Price: $29.99 USD per year (price may vary by region)</li>
              <li>Duration: 12 months</li>
              <li>Savings: 37% compared to monthly</li>
            </ul>
          </li>
        </ul>

        <h3>3.2 Payment and Billing</h3>
        <div className="warning-box">
          <h3>Important Billing Information</h3>
          <ul className="legal-list">
            <li>Payment will be charged to your iTunes Account or Google Play account at confirmation of purchase</li>
            <li>Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period</li>
            <li>Your account will be charged for renewal within 24 hours prior to the end of the current period, at the then-current subscription price</li>
            <li>Subscriptions may be managed by you, and auto-renewal may be turned off by going to your Account Settings after purchase</li>
            <li>Any unused portion of a free trial period, if offered, will be forfeited when you purchase a subscription</li>
          </ul>
        </div>

        <h3>3.3 Free Trial</h3>
        <p>
          We may offer a free trial period for new subscribers. If you don't cancel
          before the trial ends, you will be automatically charged for the subscription.
        </p>

        <h3>3.4 Managing Your Subscription</h3>
        <p><strong>For iOS/iPadOS users:</strong></p>
        <ul className="legal-list">
          <li>Go to Settings → [Your Name] → Subscriptions</li>
          <li>Select AI Vision App</li>
          <li>Cancel or modify your subscription</li>
        </ul>

        <p><strong>For Android users:</strong></p>
        <ul className="legal-list">
          <li>Open Google Play Store</li>
          <li>Tap Menu → Subscriptions</li>
          <li>Select AI Vision App</li>
          <li>Cancel or modify your subscription</li>
        </ul>

        <h3>3.5 Cancellation</h3>
        <p>
          You can cancel your subscription at any time. Cancellation takes effect at the
          end of your current billing period. You will continue to have access to premium
          features until the end of the paid period. No refunds are provided for unused
          portions of a subscription period.
        </p>
      </div>

      <div className="legal-section">
        <h2>4. Usage Limits and Fair Use</h2>

        <p>
          Free-tier users are limited to 5 AI-powered scans per day. This limit is tracked
          per device using a secure device identifier (Android ID or iOS Identifier for Vendor).
        </p>

        <div className="warning-box">
          <h3>Anti-Circumvention</h3>
          <p>
            Usage limits are enforced server-side via our Firestore database and locally via
            secure device storage (iOS Keychain / Android Keystore). Attempting to circumvent
            usage limits through app reinstallation or other means will not reset your daily count.
            Repeated attempts to circumvent limits may result in account suspension.
          </p>
        </div>
      </div>

      <div className="legal-section">
        <h2>5. User Accounts and Registration</h2>

        <h3>5.1 Account Creation</h3>
        <p>
          Account registration is available via Google Sign-In or Sign in with Apple.
          Basic detection features require an authenticated account.
        </p>

        <h3>5.2 Account Responsibility</h3>
        <p>You are responsible for:</p>
        <ul className="legal-list">
          <li>Maintaining the confidentiality of your account information</li>
          <li>All activities that occur under your account</li>
          <li>Providing accurate and up-to-date information</li>
          <li>Notifying us immediately of any unauthorized access</li>
        </ul>

        <h3>5.3 Account Termination</h3>
        <p>
          You may delete your account at any time through the App settings.
          We may terminate accounts that violate these Terms.
        </p>
      </div>

      <div className="legal-section">
        <h2>6. Acceptable Use Policy</h2>

        <div className="warning-box">
          <h3>Prohibited Uses</h3>
          <p>You agree NOT to use AI Vision App for:</p>
        </div>

        <ul className="legal-list">
          <li><strong>Illegal Activities:</strong> Any unlawful purpose or activity</li>
          <li><strong>Privacy Violations:</strong> Analyzing images without proper consent</li>
          <li><strong>Harassment:</strong> Stalking, threatening, or intimidating others</li>
          <li><strong>Harmful Content:</strong> Processing inappropriate or offensive images</li>
          <li><strong>System Abuse:</strong> Attempting to hack, reverse engineer, or exploit the service</li>
          <li><strong>Usage Circumvention:</strong> Attempting to bypass free-tier scan limits</li>
          <li><strong>Spam:</strong> Excessive or automated use of the service</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>7. AI-Generated Content Disclaimer</h2>

        <div className="warning-box">
          <h3>Important: AI Limitations</h3>
          <p>
            AI Vision uses Google's Gemini AI model to generate object descriptions, fun facts,
            estimated prices, and other information. AI-generated content is provided for
            <strong> informational and entertainment purposes only</strong>.
          </p>
        </div>

        <ul className="legal-list">
          <li>Detection results are probabilistic and may contain errors</li>
          <li>Confidence scores indicate AI certainty, not guaranteed accuracy</li>
          <li>Estimated prices are approximations and should not be relied upon for transactions</li>
          <li>Do not rely on AI Vision for critical decisions including medical, legal, financial, or safety-related determinations</li>
          <li>Plant and food identification should be verified independently before consumption</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>8. Refunds</h2>

        <p>
          All purchases are processed through Apple App Store or Google Play Store.
          Refund requests must be made through the respective platform:
        </p>
        <ul className="legal-list">
          <li><strong>iOS:</strong> Visit reportaproblem.apple.com</li>
          <li><strong>Android:</strong> Visit play.google.com/store/account</li>
        </ul>
        <p>
          No refunds are provided for unused portions of a subscription period,
          except as required by applicable law.
        </p>
      </div>

      <div className="legal-section">
        <h2>9. Intellectual Property Rights</h2>

        <h3>9.1 Our Intellectual Property</h3>
        <p>
          AI Vision App, including its design, code, user interface, and branding,
          is the intellectual property of AI Vision Team. You are granted a limited,
          non-exclusive, non-transferable license to use the App for personal,
          non-commercial purposes. You may not:
        </p>
        <ul className="legal-list">
          <li>Copy, modify, or distribute the App or its components</li>
          <li>Reverse engineer or attempt to extract source code</li>
          <li>Remove or alter copyright notices or branding</li>
          <li>Use our trademarks without permission</li>
        </ul>

        <h3>9.2 Your Content</h3>
        <p>
          You retain ownership of images you analyze through the App. By using our
          service, you grant us a limited license to process your images solely for
          providing the AI analysis service.
        </p>
      </div>

      <div className="legal-section">
        <h2>10. Privacy and Data Protection</h2>

        <p>
          Your privacy is important to us. Our collection and use of your information
          is governed by our <a href="/privacy" style={{ color: '#8B5CF6', textDecoration: 'none', fontWeight: 'bold' }}>Privacy Policy</a>, which is incorporated into these Terms
          by reference.
        </p>

        <div className="highlight-box">
          <h3>Key Privacy Points</h3>
          <ul className="legal-list">
            <li>Camera frames are processed via Google Gemini API and deleted immediately</li>
            <li>Detection history saves metadata only (labels, confidence) — not images for free users</li>
            <li>Device identifiers are used for fair-use enforcement only</li>
            <li>We do not sell your personal information</li>
            <li>You can delete your data at any time</li>
            <li>Read our full <a href="/privacy" style={{ color: '#8B5CF6' }}>Privacy Policy</a></li>
          </ul>
        </div>
      </div>

      <div className="legal-section">
        <h2>11. Disclaimers and Limitations</h2>

        <div className="warning-box">
          <h3>Service Disclaimer</h3>
          <p>
            AI Vision App is provided "AS IS" and "AS AVAILABLE." While we strive for
            accuracy, AI detection results are not guaranteed to be 100% accurate.
          </p>
        </div>

        <h3>11.1 No Warranties</h3>
        <p>We disclaim all warranties, including:</p>
        <ul className="legal-list">
          <li>Merchantability or fitness for a particular purpose</li>
          <li>Uninterrupted or error-free operation</li>
          <li>Accuracy of AI detection results, descriptions, or pricing estimates</li>
          <li>Compatibility with all devices</li>
        </ul>

        <h3>11.2 Limitation of Liability</h3>
        <p>
          To the maximum extent permitted by law, AI Vision Team shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages arising
          from your use of the App, including but not limited to damages resulting from
          inaccurate AI-generated content.
        </p>
      </div>

      <div className="legal-section">
        <h2>12. Modifications to Service and Terms</h2>

        <p>
          We may modify, suspend, or discontinue any part of the App at any time.
          We may update these Terms periodically. Material changes will be communicated
          through in-app notifications and updated on this page. Continued use after
          changes indicates acceptance of updated Terms.
        </p>
      </div>

      <div className="legal-section">
        <h2>13. Termination</h2>

        <h3>13.1 Termination by You</h3>
        <p>
          You may stop using the App and delete your account at any time through
          the App settings.
        </p>

        <h3>13.2 Termination by Us</h3>
        <p>We may suspend or terminate your access if you:</p>
        <ul className="legal-list">
          <li>Violate these Terms</li>
          <li>Attempt to circumvent usage limits or security measures</li>
          <li>Use the App in ways that could harm our service or other users</li>
          <li>Fail to pay subscription fees</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>14. Apple App Store Terms</h2>

        <div className="highlight-box">
          <h3>For iOS Users</h3>
          <p>
            If you downloaded AI Vision App from the Apple App Store, you acknowledge
            and agree that:
          </p>
        </div>

        <ul className="legal-list">
          <li>These Terms are between you and AI Vision Team only, not with Apple Inc.</li>
          <li>Apple has no obligation to furnish any maintenance and support services with respect to the App</li>
          <li>In the event of any failure of the App to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any)</li>
          <li>Apple is not responsible for addressing any claims relating to the App or your use of the App</li>
          <li>Apple and Apple's subsidiaries are third-party beneficiaries of these Terms</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>15. Governing Law</h2>

        <p>
          These Terms shall be governed by and construed in accordance with applicable laws,
          without regard to conflict of law principles. Before pursuing formal legal action,
          please contact us to resolve any disputes.
        </p>
      </div>

      <div className="legal-section">
        <h2>16. Contact Information</h2>

        <div className="highlight-box">
          <p>
            <strong>Email:</strong> <a href="mailto:cyborguser2020@gmail.com" style={{ color: '#8B5CF6' }}>cyborguser2020@gmail.com</a>
            <br />
            <strong>Support:</strong> <a href="https://aivisionsapp.web.app/support" style={{ color: '#8B5CF6' }}>https://aivisionsapp.web.app/support</a>
            <br />
            <strong>Privacy Policy:</strong> <a href="https://aivisionsapp.web.app/privacy" style={{ color: '#8B5CF6' }}>https://aivisionsapp.web.app/privacy</a>
            <br />
            <strong>Response Time:</strong> Within 72 hours
          </p>
        </div>
      </div>

      <div className="legal-section">
        <h2>17. Entire Agreement</h2>

        <p>
          These Terms, together with our Privacy Policy, constitute the entire
          agreement between you and AI Vision Team regarding your use of the App
          and supersede all prior agreements and understandings.
        </p>
      </div>
    </div>
  );
};

export default Terms;
